/**
 * TenantProfileSwitcher (PROD-568).
 *
 * Top-of-page segmented control that swaps between the two pre-configured
 * tenant profiles per master Pre-flight Decisions Tenant Profile Names:
 *   - Apex Cardiometabolic Center (`apex-cardiometabolic`)
 *   - Longevity Health Institute (`longevity-health-institute`)
 *
 * Switching is synchronous: `useTenantStore.setTenant` updates state,
 * persists to localStorage, fires the cross-store invariant (move active
 * patient to a tenant-appropriate one), and emits a `tenant_change`
 * telemetry event with `{ from_tenant, to_tenant, latency_ms,
 * correlation_id }`.
 *
 * Two surfaces added in the 2026-05-05 fixer round:
 *   1. Demo-gap field (COMPONENT-CONTRACTS §2): when a tenant config
 *      fixture fails Zod validation, the registry emits a sentinel and
 *      this component renders the missing tenant as a disabled radio
 *      with the canonical
 *      `[FIXTURE MISSING - Tenant '...' is not available in Demo Mode.
 *      Restore the fixture or contact ops.]` tooltip.
 *   2. Persistence-error revert (COMPONENT-CONTRACTS §2 live-mode
 *      failure surface): when `localStorage.setItem` throws (quota /
 *      private browsing), the store reverts the mutation and exposes
 *      `persistError`. This component subscribes to it and renders a
 *      Signal DS warning banner with the canonical message
 *      "Tenant preference could not be persisted. Switch will reset on
 *      refresh."
 *
 * Signal DS sourcing per COMPONENT-CONTRACTS §2:
 *   - Switcher trigger: SegmentedControl primitive, [GAP: signal-ds-package].
 *     Two-option SegmentedControl is preferred over Dropdown per the
 *     contract's note "only 2 tenants".
 *   - Persist-error banner: Banner variant `warning-soft`, [GAP:
 *     signal-ds-package].
 *   - Tenant label: token typography scale (DS-existing).
 *
 * HC2: Tailwind classes resolve through the Signal DS token map in
 * `src/styles/tokens.ts`. No raw hex.
 *
 * HC5: zero em dashes anywhere in the rendered copy.
 */
import { useTenantStore } from '../state/tenant.store';
import {
  TENANT_REGISTRY,
  isMissingTenantConfig,
  type TenantRegistryEntry,
} from '../data/tenant-configs';
import type { TenantId } from '../state/types';

interface TenantProfileSwitcherProps {
  className?: string;
}

interface TenantOptionProps {
  entry: TenantRegistryEntry;
  isActive: boolean;
  onSelect: (id: TenantId) => void;
}

/**
 * Build the canonical fixture-missing tooltip text per COMPONENT-CONTRACTS
 * §2 demo-gap field. Quoted verbatim - the wording is load-bearing for
 * the master v3.3 amendment requirement.
 */
function fixtureMissingTooltip(id: TenantId): string {
  return `[FIXTURE MISSING - Tenant '${id}' is not available in Demo Mode. Restore the fixture or contact ops.]`;
}

function TenantOption({ entry, isActive, onSelect }: TenantOptionProps) {
  const missing = isMissingTenantConfig(entry);
  const handleClick = () => {
    if (missing) return;
    onSelect(entry.id);
  };
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isActive}
      aria-disabled={missing ? 'true' : undefined}
      data-tenant-id={entry.id}
      data-active={isActive ? 'true' : 'false'}
      data-missing={missing ? 'true' : 'false'}
      disabled={missing}
      onClick={handleClick}
      title={
        missing
          ? fixtureMissingTooltip(entry.id)
          : `Switch to ${entry.displayName}`
      }
      className={[
        'inline-flex items-center gap-2 rounded-signal-pill px-gutter-md py-1 text-signal-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-sidebar focus:ring-sidebar-tab-active',
        missing
          ? 'cursor-not-allowed bg-sidebar-hover text-sidebar-muted opacity-60'
          : isActive
            ? 'bg-sidebar-tab-active text-text-inverse'
            : 'bg-sidebar-hover text-sidebar-muted hover:text-text-inverse',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'inline-block h-1.5 w-1.5 rounded-signal-pill',
          isActive && !missing ? 'bg-text-inverse' : 'bg-sidebar-muted',
        ].join(' ')}
      />
      {entry.displayName}
      {missing && (
        <span
          aria-hidden="true"
          className="ml-1 rounded-signal-sm bg-status-draft-bg px-1.5 py-0.5 text-signal-xs font-semibold uppercase tracking-wide text-status-draft-fg"
        >
          MISSING
        </span>
      )}
    </button>
  );
}

interface PersistErrorBannerProps {
  message: string;
  onDismiss: () => void;
}

function PersistErrorBanner({ message, onDismiss }: PersistErrorBannerProps) {
  return (
    <div
      role="alert"
      data-testid="tenant-persist-error"
      className="flex items-start gap-gutter-sm rounded-signal-md border border-status-draft bg-status-draft-bg px-gutter-md py-gutter-sm text-signal-sm text-status-draft-fg"
    >
      <span aria-hidden="true" className="font-semibold uppercase tracking-wide">
        Warning
      </span>
      <span className="flex-1">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="text-signal-xs font-semibold uppercase tracking-wide underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-status-draft"
        aria-label="Dismiss tenant persistence warning"
      >
        Dismiss
      </button>
    </div>
  );
}

export function TenantProfileSwitcher({ className }: TenantProfileSwitcherProps) {
  const tenantId = useTenantStore((s) => s.tenantId);
  const setTenant = useTenantStore((s) => s.setTenant);
  const persistError = useTenantStore((s) => s.persistError);
  const clearPersistError = useTenantStore((s) => s.clearPersistError);

  return (
    <div
      className={['flex flex-col gap-gutter-sm', className ?? '']
        .filter(Boolean)
        .join(' ')}
    >
      {persistError !== null && (
        <PersistErrorBanner
          message={persistError}
          onDismiss={clearPersistError}
        />
      )}
      <div
        role="radiogroup"
        aria-label="Tenant profile"
        data-testid="tenant-profile-switcher"
        className="inline-flex items-center gap-1 rounded-signal-pill border border-sidebar-border bg-sidebar p-0.5"
      >
        {TENANT_REGISTRY.map((entry) => (
          <TenantOption
            key={entry.id}
            entry={entry}
            isActive={entry.id === tenantId && !isMissingTenantConfig(entry)}
            onSelect={setTenant}
          />
        ))}
      </div>
    </div>
  );
}
